// Package imports:
import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
// Service imports:
import { formatNumber, convertToPercentage } from '../../services/utils';
// Type imports:
import { IApiLmdGenderLensSnapshot } from '../../types/HlutabrefTypes';

type Language = 'is' | 'en';

interface IProps {
    data: IApiLmdGenderLensSnapshot[],
    lang?: Language,
    height?: number,
    showHeader?: boolean
}

const getColor = (pct: number | null) => {
    return pct === null
        ? '#F1F2F5'
        : pct < 0.1
        ? '#4353D8'
        : pct < 0.2
        ? '#5A8EEE'
        : pct < 0.3
        ? '#55CCA1'
        : pct < 0.4
        ? '#FBD825'
        : pct < 0.5
        ? '#FAB530'
        : '#EB5CA4';
}

const GEMMAQChart: React.FC<IProps> = ({
    data,
    lang = 'is',
    height = 300,
    showHeader
}) => {
    // Div references.
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (data.length > 0 && divRef.current !== null) {
            Highcharts.chart(divRef.current, {
                chart: {
                    type: 'pie',
                    height: 305,
                    style: {
                        fontFamily: "Roboto"
                    },
                    animation: false
                },
                title: {
                    text: (lang === 'is' ? formatNumber(data[0].GenderLensScore, null, 1) : data[0].GenderLensScore?.toString()) ?? undefined,
                    align: 'center',
                    x: 0,
                    floating: true,
                    y: 152.5,
                    style: {
                        fontSize: '40px',
                        fontWeight: 'bold',
                        fontFamily: 'Roboto'
                    }
                },
                subtitle: showHeader ? {
                    text: data[0].IssuerName ?? '',
                    verticalAlign: 'top',
                    style: {
                        fontSize: '18px',
                        color: '#000'
                    }
                } : {},
                plotOptions: {
                    pie: {
                        size: showHeader ? '80%' : '90%',
                        center: ['50%','50%'],
                        animation: false
                    }
                },
                responsive: {
                    rules: [{
                        condition: {
                            callback: function() {
                                return window.matchMedia('(max-width: 767px)').matches;
                            }
                        },
                        chartOptions: {
                            chart: {
                                height: 265
                            },
                            title: {
                                style: {
                                    fontSize: '30px'
                                },
                                y: 132.5
                            }
                        }
                    }]
                },
                series: [{
                    type: 'pie',
                    innerSize: '40%',
                    data: [{
                        name: '',
                        y: 1,
                        color: data[0].ChairmanOfTheBoard === 'male' ? getColor(0) : getColor(1)
                    }, {
                        name: '',
                        y: 1,
                        color: getColor(data[0].WomenOnBoardPer)
                    }, {
                        name: '',
                        y: 1,
                        color: getColor(data[0].WomenDirectorsPer)
                    }, {
                        name: '',
                        y: 1,
                        color: data[0].Ceo === 'male' ? getColor(0) : getColor(1)
                    }],
                    dataLabels: {
                        enabled: false
                    }
                }],
                exporting: {
                    enabled: false
                },
                credits: {
                    enabled: false
                },
                tooltip: {
                    enabled: false
                },
                legend: {
                    enabled: false
                }
            }, () => {});
        } else if (divRef.current !== null) {
            Highcharts.chart(divRef.current, {
                chart: {
                    type: 'pie',
                    height: height,
                    style: {
                        fontFamily: "Roboto"
                    },
                    animation: false
                },
                title: {
                    text: lang === 'is' ? 'Gögn vantar' : 'No data',
                    align: 'center',
                    x: 0,
                    floating: true,
                    y: 0.48*height,
                    style: {
                        fontSize: '16px',
                        fontWeight: 'bold',
                        fontFamily: 'Roboto'
                    }
                },
                plotOptions: {
                    pie: {
                        size: '90%',
                        center: ['50%','50%'],
                        animation: false
                    }
                },
                series: [{
                    type: 'pie',
                    innerSize: '40%',
                    data: [{
                        name: '',
                        y: 1,
                        color: getColor(null)
                    }, {
                        name: '',
                        y: 1,
                        color: getColor(null)
                    }, {
                        name: '',
                        y: 1,
                        color: getColor(null)
                    }, {
                        name: '',
                        y: 1,
                        color: getColor(null)
                    }],
                    dataLabels: {
                        enabled: false
                    }
                }],
                exporting: {
                    enabled: false
                },
                credits: {
                    enabled: false
                },
                tooltip: {
                    enabled: false
                },
                legend: {
                    enabled: false
                }
            }, () => {});
        }
    }, []);

    return (
        <div className='KCL_GEMMAQ-chart'>
            <div ref={divRef}></div>
            <ul className="GEMMAQ-chart__legend">
                <li>
                    <p>{lang === 'is' ? 'Forstjóri' : 'CEO'}</p>
                    <p>
                        <strong>
                            {lang === 'is'
                                ? (data[0]?.Ceo === 'male' ? 'Karl' : data[0]?.Ceo === 'female' ? 'Kona' : '-')
                                : (data[0]?.Ceo === 'male' ? 'Male' : data[0]?.Ceo === 'female' ? 'Female' : '-')
                            }
                        </strong>
                        <span className="color" style={{
                            background: getColor(data[0]?.Ceo === 'male' ? 0 : data[0]?.Ceo === 'female' ? 1 : null)
                        }}></span>
                    </p>
                </li>
                <li>
                    <p>{lang === 'is' ? 'Stjórnarformaður' : 'Chairman of the board'}</p>
                    <p>
                        <strong>
                            {lang === 'is'
                                ? (data[0]?.ChairmanOfTheBoard === 'male' ? 'Karl' : data[0]?.ChairmanOfTheBoard === 'female' ? 'Kona' : '-')
                                : (data[0]?.ChairmanOfTheBoard === 'male' ? 'Male' : data[0]?.ChairmanOfTheBoard === 'female' ? 'Female' : '-')
                            }
                        </strong>
                        <span className="color" style={{
                            background: getColor(data[0]?.ChairmanOfTheBoard === 'male' ? 0 : data[0]?.ChairmanOfTheBoard === 'female' ? 1 : null)
                        }}></span>
                    </p>
                </li>
                <li>
                    <p>{lang === 'is' ? 'Hlutfall kvenna í\nframkvæmdastjórn' : 'Top executives'}</p>
                    <p>
                        <strong>{convertToPercentage(data[0]?.WomenDirectorsPer ?? null, true, 0)}</strong>
                        <span className="color" style={{
                            background: getColor(data[0]?.WomenDirectorsPer ?? null)
                        }}></span>
                    </p>
                </li>
                <li>
                    <p>{lang === 'is' ? 'Hlutfall kvenna í\nstjórn' : 'Board of directors'}</p>
                    <p>
                        <strong>{convertToPercentage(data[0]?.WomenOnBoardPer ?? null, true, 0)}</strong>
                        <span className="color" style={{
                            background: getColor(data[0]?.WomenOnBoardPer ?? null)
                        }}></span>
                    </p>
                </li>
            </ul>
        </div>
    );
}

export default GEMMAQChart;